import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from "recharts";
import { Box } from "@mui/material";
import ChartSummaryRows from "./ChartSummaryRows";
import { getLocalDateOrFallback } from "../utils/localDateTime";

const MIN_DATE = "2017-01-01";
const REPORTING_CATEGORY_SERIES = [
  {
    key: "nearMiss",
    fieldName: "has_never_miss",
    color: "#4a90d9",
    labels: { en: "Near miss", dk: "Near miss" },
  },
  {
    key: "prevention",
    fieldName: "has_prevention",
    color: "#6aaa2a",
    labels: { en: "Prevention", dk: "Forebyggelse" },
  },
  {
    key: "doubtAboutReporting",
    fieldName: "has_doubt_about_reporting",
    color: "#3F3F3F",
    labels: { en: "Doubt about reporting", dk: "Tvivl om rapportering" },
  },
];

const isTruthyFlag = (value) => {
  if (value === true) return true;
  if (value === false || value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  return normalized === "1" || normalized === "true";
};

const parseDateStrUTC = (dateStr) => {
  const [year, month, day] = String(dateStr).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const formatDateStrUTC = (dateObj) => {
  const year = dateObj.getUTCFullYear();
  const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateRangeStringsInclusive = (fromStr, toStr) => {
  const safeFrom = fromStr || MIN_DATE;
  const safeTo = toStr || formatDateStrUTC(new Date());
  const startStr = safeFrom <= safeTo ? safeFrom : safeTo;
  const endStr = safeFrom <= safeTo ? safeTo : safeFrom;
  const start = parseDateStrUTC(startStr);
  const end = parseDateStrUTC(endStr);
  const dates = [];
  const cursor = new Date(start);

  while (cursor <= end) {
    dates.push(formatDateStrUTC(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return dates;
};

const getLastNDatesInclusive = (days) => {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(end.getUTCDate() - Math.max(0, days - 1));
  return getDateRangeStringsInclusive(
    formatDateStrUTC(start),
    formatDateStrUTC(end)
  );
};

const formatTickLabel = (dateStr) => {
  const date = new Date(`${dateStr}T00:00:00Z`);
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = date
    .toLocaleString("en-GB", { month: "short", timeZone: "UTC" })
    .replace(".", "")
    .toUpperCase();
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

const buildTickIndexes = (length) => {
  if (length <= 0) return [];
  if (length <= 12) {
    return Array.from({ length }, (_, index) => index);
  }

  const targetTicks = 8;
  const step = Math.max(1, Math.ceil((length - 1) / (targetTicks - 1)));
  const indexes = [];

  for (let index = 0; index < length; index += step) {
    indexes.push(index);
  }

  if (indexes[indexes.length - 1] !== length - 1) {
    indexes.push(length - 1);
  }

  return indexes;
};

const CustomTooltip = ({ active, payload, label, lang }) => {
  if (!active || !payload?.length) return null;

  return (
    <Box
      sx={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        p: 1.5,
        fontSize: 12,
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((item) => (
        <div key={item.dataKey} style={{ color: item.fill }}>
          {item.name}: {item.value}
        </div>
      ))}
    </Box>
  );
};

const ReportingCategoryDateChart = ({
  records = [],
  dateFrom = "",
  dateTo = "",
  showNearMiss = true,
  showPrevention = true,
  showDoubtAboutReporting = true,
  lang = "en",
  height = 600,
}) => {
  const labels = useMemo(
    () => ({
      count: lang === "dk" ? "Antal" : "Count",
    }),
    [lang]
  );
  const reportingCategoryLabels = useMemo(
    () =>
      REPORTING_CATEGORY_SERIES.reduce((accumulator, series) => {
        accumulator[series.key] = series.labels[lang] ?? series.labels.en;
        return accumulator;
      }, {}),
    [lang]
  );
  const reportingCategoryVisibility = useMemo(
    () => ({
      nearMiss: showNearMiss,
      prevention: showPrevention,
      doubtAboutReporting: showDoubtAboutReporting,
    }),
    [showNearMiss, showPrevention, showDoubtAboutReporting]
  );

  const dateSeries = useMemo(
    () =>
      getDateRangeStringsInclusive(
        dateFrom || MIN_DATE,
        dateTo || formatDateStrUTC(new Date())
      ),
    [dateFrom, dateTo]
  );

  const chartData = useMemo(() => {
    const countsByDate = new Map();

    records.forEach((record) => {
      const date = getLocalDateOrFallback(record.utc_tid_skannet);
      if (!date) return;

      const current =
        countsByDate.get(date) ??
        REPORTING_CATEGORY_SERIES.reduce((accumulator, series) => {
          accumulator[series.key] = 0;
          return accumulator;
        }, {});

      REPORTING_CATEGORY_SERIES.forEach((series) => {
        if (isTruthyFlag(record?.[series.fieldName])) {
          current[series.key] += 1;
        }
      });

      countsByDate.set(date, current);
    });

    if (countsByDate.size > 0) {
      return Array.from(countsByDate.entries())
        .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
        .map(([date, counts]) =>
          REPORTING_CATEGORY_SERIES.reduce(
            (row, series) => {
              row[series.key] = reportingCategoryVisibility[series.key]
                ? counts[series.key]
                : 0;
              return row;
            },
            {
              date,
              tickLabel: formatTickLabel(date),
              tooltipLabel: formatTickLabel(date),
            }
          )
        );
    }

    const fallbackDateSeries =
      dateFrom || dateTo ? dateSeries : getLastNDatesInclusive(7);

    return fallbackDateSeries.map((date) => {
      const counts =
        countsByDate.get(date) ??
        REPORTING_CATEGORY_SERIES.reduce((accumulator, series) => {
          accumulator[series.key] = 0;
          return accumulator;
        }, {});

      return REPORTING_CATEGORY_SERIES.reduce(
        (row, series) => {
          row[series.key] = reportingCategoryVisibility[series.key]
            ? counts[series.key]
            : 0;
          return row;
        },
        {
          date,
          tickLabel: formatTickLabel(date),
          tooltipLabel: formatTickLabel(date),
        }
      );
    });
  }, [records, dateSeries, dateFrom, dateTo, reportingCategoryVisibility]);

  const tickIndexes = useMemo(
    () => buildTickIndexes(chartData.length),
    [chartData.length]
  );
  const xTicks = useMemo(
    () => tickIndexes.map((index) => chartData[index]?.date).filter(Boolean),
    [chartData, tickIndexes]
  );

  const totals = useMemo(
    () =>
      REPORTING_CATEGORY_SERIES.reduce((accumulator, series) => {
        accumulator[series.key] = chartData.reduce(
          (sum, row) => sum + Number(row[series.key] ?? 0),
          0
        );
        return accumulator;
      }, {}),
    [chartData]
  );

  const summaryRows = useMemo(() => {
    return REPORTING_CATEGORY_SERIES.filter(
      (series) => reportingCategoryVisibility[series.key]
    ).map((series) => ({
      label: reportingCategoryLabels[series.key],
      value: totals[series.key] ?? 0,
      color: series.color,
    }));
  }, [reportingCategoryLabels, reportingCategoryVisibility, totals]);

  const showValueLabels = chartData.length <= 31;

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: 2,
        p: 2,
        border: "1px solid #eee",
      }}
    >
      <div style={{ width: "100%", height }}>
        <ChartSummaryRows rows={summaryRows} />
        <ResponsiveContainer width="100%" height="90%">
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 30, left: 20, bottom: 60 }}
            barCategoryGap="25%"
            barGap={2}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#eee"
            />
            <XAxis
              dataKey="date"
              ticks={xTicks}
              tickFormatter={formatTickLabel}
              tick={{ fontSize: 11, fill: "#666" }}
              angle={-90}
              textAnchor="end"
              interval={0}
              minTickGap={24}
              height={110}
              tickMargin={10}
            />
            <YAxis
              allowDecimals={false}
              label={{
                value: labels.count,
                angle: -90,
                position: "insideLeft",
                offset: -4,
                style: { fontSize: 12, fill: "#666" },
              }}
              tick={{ fontSize: 11, fill: "#666" }}
            />
            <Tooltip
              content={<CustomTooltip lang={lang} />}
              labelFormatter={(value) => formatTickLabel(value)}
            />

            {REPORTING_CATEGORY_SERIES.map((series) =>
              reportingCategoryVisibility[series.key] ? (
                <Bar
                  key={series.key}
                  dataKey={series.key}
                  name={reportingCategoryLabels[series.key]}
                  fill={series.color}
                  radius={[3, 3, 0, 0]}
                  isAnimationActive={false}
                >
                  {showValueLabels ? (
                    <LabelList
                      dataKey={series.key}
                      position="top"
                      formatter={(value) => (value ? value : "")}
                      style={{ fontSize: 10, fill: "#555" }}
                    />
                  ) : null}
                </Bar>
              ) : null
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Box>
  );
};

export default ReportingCategoryDateChart;
